import React, { useState, useEffect } from 'react'
import { Project } from '../../../shared/types'
import { TimeEntry } from '../services/timeTracking'
import { CreateProjectInput, UpdateProjectInput, projectService } from '../services/projects'
import { useAuth } from '../hooks/useAuth'
import { useRoleManagement } from '../hooks/useRoleManagement'
import { useUserSettingsContext } from '../hooks/useUserSettingsContext'
import ProjectStats from './ProjectStats'
import CustomScrollbar from './CustomScrollbar'
import CustomDropdown from './CustomDropdown'
import { getCurrencyOptions, getCurrencySymbol, formatHourlyRate } from '../utils/currency'

interface ProjectFormProps {
  mode: 'create' | 'edit'
  project?: Project // Required for edit mode
  onSubmit: (data: CreateProjectInput | UpdateProjectInput) => Promise<void>
  onCancel: () => void
  loading: boolean
  timeEntries?: TimeEntry[]
  getCurrentDuration?: (entry: TimeEntry) => number
  formatDuration?: (seconds: number) => string
}

export default function ProjectForm({
  mode,
  project,
  onSubmit,
  onCancel,
  loading,
  timeEntries = [],
  getCurrentDuration = () => 0,
  formatDuration = () => '0h 0m'
}: ProjectFormProps) {
  const { user } = useAuth()
  const { users: allUsers, fetchUsers, loading: usersLoading } = useRoleManagement()
  const { displaySettings } = useUserSettingsContext()

  // Form state
  const [name, setName] = useState(mode === 'edit' && project ? project.name : '')
  const [description, setDescription] = useState(mode === 'edit' && project ? project.description || '' : '')
  const [color, setColor] = useState(mode === 'edit' && project ? project.color || '#3B82F6' : '#3B82F6')
  const [currency, setCurrency] = useState(mode === 'edit' && project ? project.currency || displaySettings.defaultCurrency : displaySettings.defaultCurrency)
  const [billable, setBillable] = useState(mode === 'edit' && project ? project.billable || false : false)
  const [hourlyRate, setHourlyRate] = useState<string>(mode === 'edit' && project ? project.hourlyRate?.toString() || '' : '')
  const [budget, setBudget] = useState<string>(mode === 'edit' && project ? project.budget?.toString() || '' : '')
  const [budgetType, setBudgetType] = useState<'monetary' | 'hours' | null>(
    mode === 'edit' && project && project.budget 
      ? (project.budgetType || (project.budget && project.budget > 0 ? 'monetary' : null))
      : null
  )
  const [budgetHourLimit, setBudgetHourLimit] = useState<string>(mode === 'edit' && project ? project.budgetHourLimit?.toString() || '60' : '60')
  const [budgetExcludedDays, setBudgetExcludedDays] = useState<number[]>(
    mode === 'edit' && project && project.budgetExcludedDays !== undefined
      ? project.budgetExcludedDays
      : [0, 6]
  )
  const [locations, setLocations] = useState<{ name: string; hourlyRate?: number }[]>(
    mode === 'edit' && project 
      ? project.locations?.map(loc => typeof loc === 'string' ? { name: loc } : loc) || [{ name: 'Remote' }]
      : [{ name: 'Remote' }]
  )
  const [newLocation, setNewLocation] = useState('')
  const [newLocationRate, setNewLocationRate] = useState<string>('')
  const [projectType, setProjectType] = useState<'personal' | 'shared'>(mode === 'edit' && project ? project.type || 'personal' : 'personal')
  const [selectedUsers, setSelectedUsers] = useState<string[]>(mode === 'edit' && project ? project.sharedUsers || [] : [])
  const [billableError, setBillableError] = useState<string | null>(null)
  const [locationsWithEntries, setLocationsWithEntries] = useState<string[]>([])

  // Helper function to count entries per location
  const getLocationEntryCount = (locationName: string): number => {
    if (!timeEntries || !project?.id) return 0
    return timeEntries.filter(
      entry => entry.projectId === project.id && entry.location === locationName
    ).length
  }

  // Compute locations with entries from timeEntries (client-side)
  // Fallback to API call if timeEntries not available
  useEffect(() => {
    if (mode === 'edit' && project?.id) {
      // First, try to compute from local timeEntries
      if (timeEntries && timeEntries.length > 0) {
        const projectEntries = timeEntries.filter(entry => entry.projectId === project.id)
        const locationNames = new Set<string>()
        projectEntries.forEach(entry => {
          if (entry.location) {
            locationNames.add(entry.location)
          }
        })
        setLocationsWithEntries(Array.from(locationNames))
      } else {
        // Fallback to API call if timeEntries not available
        projectService.getLocationsWithEntries(project.id)
          .then(response => setLocationsWithEntries(response.locations))
          .catch(error => console.error('Failed to fetch locations with entries:', error))
      }
    }
  }, [mode, project?.id, timeEntries])

  // Load users when component mounts if user is admin
  useEffect(() => {
    if (user?.role === 'admin' && allUsers.length === 0) {
      fetchUsers(1, 100) // Load first 100 users
    }
  }, [user?.role, allUsers.length, fetchUsers])

  // Load users for shared project selection
  useEffect(() => {
    if (user?.role === 'admin' && projectType === 'shared' && allUsers.length === 0) {
      fetchUsers(1, 100) // Load first 100 users
    }
  }, [user?.role, projectType, allUsers.length, fetchUsers])

  // Handle ESC key to close the form
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onCancel])

  // Clear billable error when relevant fields change
  useEffect(() => {
    setBillableError(null)
  }, [billable, hourlyRate, locations])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      alert('Please enter a project name')
      return
    }

    if (projectType === 'shared' && selectedUsers.length === 0) {
      alert('Please select at least one user for shared projects')
      return
    }

    // Validate that billable projects have at least one rate defined
    if (billable) {
      const hasMainRate = hourlyRate && parseFloat(hourlyRate) > 0
      const hasLocationRate = locations.some(loc => loc.hourlyRate && loc.hourlyRate > 0)

      if (!hasMainRate && !hasLocationRate) {
        setBillableError('Billable projects must have either a Hourly Rate or at least one location with a rate')
        return
      }
    }

    // Clear any previous billable error
    setBillableError(null)

    if (mode === 'create') {
      const input: CreateProjectInput = {
        name: name.trim(),
        description: description.trim() || undefined,
        color: color,
        billable: billable,
        currency: currency,
        hourlyRate: hourlyRate ? parseFloat(hourlyRate) : undefined,
        budget: budget ? parseFloat(budget) : undefined,
        budgetType: budget && budgetType ? budgetType : undefined,
        budgetHourLimit: budgetType === 'hours' && budget ? parseInt(budgetHourLimit) : undefined,
        budgetExcludedDays: budgetType === 'hours' && budget ? budgetExcludedDays : undefined,
        locations: locations,
        type: projectType,
        sharedUsers: projectType === 'shared' ? selectedUsers : undefined,
      }
      await onSubmit(input)
    } else {
      const input: UpdateProjectInput = {
        name: name.trim(),
        description: description.trim() || undefined,
        color: color,
        billable: billable,
        currency: currency,
        hourlyRate: hourlyRate ? parseFloat(hourlyRate) : undefined,
        budget: budget ? parseFloat(budget) : undefined,
        budgetType: budget && budgetType ? budgetType : undefined,
        budgetHourLimit: budgetType === 'hours' && budget ? parseInt(budgetHourLimit) : undefined,
        budgetExcludedDays: budgetType === 'hours' && budget ? budgetExcludedDays : undefined,
        locations: locations,
        type: projectType,
        sharedUsers: projectType === 'shared' ? selectedUsers : undefined,
      }
      await onSubmit(input)
    }
  }

  const handleUserToggle = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  return (
    <div 
      className="fixed inset-0 bg-gradient-to-br from-black/20 via-blue-900/5 to-indigo-900/8 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onCancel();
        }
      }}
    >
      <CustomScrollbar
        className="bg-gradient-to-br from-white/95 via-blue-50/25 to-indigo-50/35 backdrop-blur-sm border border-gray-200/80 shadow-xl max-w-md w-full p-6 my-8 max-h-[90vh] overflow-y-auto"
        style={{
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          borderImage: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(99, 102, 241, 0.08)) 1'
        }}
        onClick={(e) => e.stopPropagation()}
      >

        
        {mode === 'edit' && project && (
          <ProjectStats
            project={project}
            timeEntries={timeEntries}
            getCurrentDuration={getCurrentDuration}
            formatDuration={formatDuration}
          />
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Project Name *
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300/70 bg-white/95 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/60 text-sm transition-all duration-200"
              placeholder="Enter project name"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300/70 bg-white/95 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/60 text-sm transition-all duration-200"
              placeholder="Enter project description"
              rows={3}
              maxLength={500}
            />
          </div>

          <div>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                id="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-12 h-10 border border-gray-300 rounded-md cursor-pointer"
              />
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300/70 bg-white/95 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/60 text-sm transition-all duration-200"
                placeholder="#3B82F6"
              />
            </div>
          </div>

          {/* Visual Separator */}
          <div className="border-t border-gray-200 my-4"></div>

          {/* Project Type Selection - Admin Only */}
          {user?.role === 'admin' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Type
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={projectType === 'personal'}
                    onChange={(e) => setProjectType(e.target.checked ? 'personal' : 'shared')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Personal</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={projectType === 'shared'}
                    onChange={(e) => setProjectType(e.target.checked ? 'shared' : 'personal')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Shared</span>
                </label>
              </div>
            </div>
          )}

          {/* User Selection for Shared Projects */}
          {user?.role === 'admin' && projectType === 'shared' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Share with Users *
              </label>
              {usersLoading || allUsers.length === 0 ? (
                <div className="border border-gray-300/70 rounded-md p-3 bg-white/90 backdrop-blur-sm">
                  <p className="text-sm text-gray-500">
                    {usersLoading ? 'Loading users...' : 'No users available'}
                  </p>
                </div>
              ) : (
                <div className="max-h-32 overflow-y-auto border border-gray-300/70 rounded-md p-2 space-y-1 bg-white/90 backdrop-blur-sm">
                  {allUsers.map((userItem) => (
                    <label key={userItem.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(userItem.id)}
                        onChange={() => handleUserToggle(userItem.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">{userItem.name}</span>
                      <span className="text-xs text-gray-500">({userItem.email})</span>
                    </label>
                  ))}
                </div>
              )}
              {selectedUsers.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-gray-600 mb-1">Selected users ({selectedUsers.length}):</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedUsers.map((userId) => {
                      const userItem = allUsers.find(u => u.id === userId)
                      return userItem ? (
                        <span key={userId} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                          {userItem.name}
                          <button
                            type="button"
                            onClick={() => handleUserToggle(userId)}
                            className="ml-1 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ) : null
                    })}
                  </div>
                </div>
              )}
              {selectedUsers.length === 0 && projectType === 'shared' && (
                <p className="text-xs text-red-600 mt-1">Please select at least one user</p>
              )}
            </div>
          )}

          <div className="flex items-center">
            <input
              type="checkbox"
              id="billable"
              checked={billable}
              onChange={(e) => setBillable(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="billable" className="ml-2 block text-sm text-gray-700">
              Billable project
            </label>
          </div>

          {billableError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
              {billableError}
            </div>
          )}

          {billable && (
            <>
              <div>
                <CustomDropdown
                  id="currency"
                  label="Currency"
                  placeholder="Select currency"
                  options={getCurrencyOptions()}
                  selectedValues={[currency]}
                  onSelectionChange={(values) => setCurrency(values[0] || 'EUR')}
                  multiSelect={false}
                />
              </div>
              <div>
                <label htmlFor="hourlyRate" className="block text-sm font-medium text-gray-700 mb-1">
                  Hourly Rate ({getCurrencySymbol(currency)})
                </label>
                <input
                  type="number"
                  id="hourlyRate"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300/70 bg-white/95 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/60 text-sm transition-all duration-200"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="space-y-2">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={budgetType === 'monetary'}
                      onChange={(e) => setBudgetType(e.target.checked ? 'monetary' : null)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Monetary Budget ({getCurrencySymbol(currency)})</span>
                  </label>
                  {budgetType === 'monetary' && (
                    <div className="mt-2">
                      <input
                        type="number"
                        id="budget"
                        value={budget}
                        onChange={(e) => setBudget(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300/70 bg-white/95 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/60 text-sm transition-all duration-200"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={budgetType === 'hours'}
                      onChange={(e) => setBudgetType(e.target.checked ? 'hours' : null)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Daily Hours Budget (hours/day)</span>
                  </label>
                  {budgetType === 'hours' && (
                    <div className="mt-2">
                      <input
                        type="number"
                        id="budget"
                        value={budget}
                        onChange={(e) => setBudget(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300/70 bg-white/95 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/60 text-sm transition-all duration-200"
                        placeholder="8"
                        min="0"
                        max="24"
                        step="0.5"
                      />
                    </div>
                  )}
                </div>
              </div>

              {budgetType === 'hours' && budget && (
                <div>
                  <label htmlFor="budgetHourLimit" className="block text-sm font-medium text-gray-700 mb-1">
                    Budget Hour Limit Tolerance (minutes)
                  </label>
                  <input
                    type="number"
                    id="budgetHourLimit"
                    value={budgetHourLimit}
                    onChange={(e) => setBudgetHourLimit(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300/70 bg-white/95 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/60 text-sm transition-all duration-200"
                    placeholder="60"
                    min="1"
                    max="1440"
                    step="1"
                  />

                </div>
              )}

              {budgetType === 'hours' && budget && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Excluded Days from hourly budget warnings
                  </label>
                  <div className="flex gap-1 flex-wrap">
                    {[
                      { label: 'Mon', value: 1 }, { label: 'Tue', value: 2 },
                      { label: 'Wed', value: 3 }, { label: 'Thu', value: 4 },
                      { label: 'Fri', value: 5 }, { label: 'Sat', value: 6 },
                      { label: 'Sun', value: 0 },
                    ].map(({ label, value }) => {
                      const active = budgetExcludedDays.includes(value)
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setBudgetExcludedDays(
                            active ? budgetExcludedDays.filter(d => d !== value) : [...budgetExcludedDays, value]
                          )}
                          className={`px-2 py-1 text-xs rounded border transition-colors ${
                            active
                              ? 'bg-blue-100 border-blue-400 text-blue-800 font-semibold'
                              : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Visual Separator */}
          <div className="border-t border-gray-200 my-4"></div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Locations
            </label>
            <div className="space-y-2">
              {locations.map((location, index) => {
                const entryCount = getLocationEntryCount(location.name);
                const hasEntries = entryCount > 0;

                return (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="flex-1 px-3 py-2 bg-white/90 backdrop-blur-sm border border-gray-200/70 shadow-sm text-sm transition-all duration-200 hover:bg-white/95">
                      <div className="font-medium">{location.name}</div>
                      {billable && (
                        <div className="flex items-center space-x-2 mt-1">
                          <input
                            type="number"
                            value={location.hourlyRate?.toString() || ''}
                            onChange={(e) => {
                              const newLocations = [...locations];
                              newLocations[index] = {
                                ...newLocations[index],
                                hourlyRate: e.target.value ? parseFloat(e.target.value) : undefined
                              };
                              setLocations(newLocations);
                            }}
                            placeholder={`${getCurrencySymbol(currency)}/h`}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-xs"
                            min="0"
                            step="0.01"
                            title={hasEntries ? `⚠️ Changing this rate will affect budget calculations for ${entryCount} time ${entryCount === 1 ? 'entry' : 'entries'}` : undefined}
                          />
                          {location.hourlyRate !== undefined && location.hourlyRate !== null && (
                            <span className="text-xs text-gray-500">
                              {formatHourlyRate(location.hourlyRate, currency)}
                            </span>
                          )}
                          {hasEntries && location.hourlyRate !== undefined && location.hourlyRate !== null && (
                            <span
                              className="text-amber-500"
                              title={`⚠️ Changing this rate will affect budget calculations for ${entryCount} time ${entryCount === 1 ? 'entry' : 'entries'}`}
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  <button
                    type="button"
                    onClick={() => setLocations(locations.filter((_, i) => i !== index))}
                    className={`focus:outline-none ${
                      locations.length === 1 || locationsWithEntries.includes(location.name)
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-red-500 hover:text-red-700'
                    }`}
                    disabled={locations.length === 1 || locationsWithEntries.includes(location.name)}
                    title={
                      locationsWithEntries.includes(location.name)
                        ? `Cannot delete "${location.name}" - it has associated time entries`
                        : locations.length === 1
                        ? 'At least one location is required'
                        : `Delete location "${location.name}"`
                    }
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              );
            })}
              <div className="flex items-center space-x-2">
                <div className="flex-1 px-3 py-2 border border-gray-300/70 bg-white/95 backdrop-blur-sm transition-all duration-200">
                  <input
                    type="text"
                    value={newLocation}
                    onChange={(e) => setNewLocation(e.target.value)}
                    placeholder="Add new location..."
                    className="w-full bg-transparent focus:outline-none text-sm"
                  />
                  {billable && (
                    <div className="flex items-center space-x-2 mt-1">
                      <input
                        type="number"
                        value={newLocationRate}
                        onChange={(e) => setNewLocationRate(e.target.value)}
                        placeholder={`${getCurrencySymbol(currency)}/h`}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-xs"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (newLocation.trim() && !locations.some(loc => loc.name === newLocation.trim())) {
                      setLocations([...locations, { name: newLocation.trim(), hourlyRate: newLocationRate ? parseFloat(newLocationRate) : undefined }])
                      setNewLocation('')
                      setNewLocationRate('')
                    }
                  }}
                  disabled={!newLocation.trim() || locations.some(loc => loc.name === newLocation.trim())}
                  className="px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-all duration-200"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300/70 text-gray-700 bg-white/95 backdrop-blur-sm hover:bg-gray-50/90 focus:outline-none focus:ring-2 focus:ring-gray-500/50 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim() || (projectType === 'shared' && selectedUsers.length === 0) || (projectType === 'shared' && usersLoading)}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {loading ? (mode === 'create' ? 'Creating...' : 'Updating...') : (mode === 'create' ? 'Create Project' : 'Update Project')}
            </button>
          </div>
        </form>
      </CustomScrollbar>
    </div>
  )
} 